import React from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary } from '@/components/errorBoundary';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { VatReportPageView } from './VatReportPageView';
import { useVatReportPage } from './useVatReportPage';

/**
 * `/accounting/statements/vat` — the VAT report + settlement screen. Owns its
 * data hook and an `ErrorBoundary`; the throw lives in the inner Content child
 * (the repo's self-served page pattern).
 */
export const VatReportPage: React.FC = () => {
    const page = useVatReportPage();
    const { t } = useTranslation('pages');

    const copy = React.useMemo(
        () => ({
            errorTitle: t('vat.error.title'),
            errorMessage: t('vat.error.message'),
            errorRetry: t('vat.error.retry'),
        }),
        [t],
    );

    return (
        <div
            className="flex w-full flex-col gap-6"
            {...getNodeTestIdProp('vat-report-page')}
        >
            <ErrorBoundary
                data-testid="vat-error-boundary"
                fallbackMessage={copy.errorMessage}
                id="vat-error-boundary"
                onRetry={page.onRetry}
                resetKeys={[page.error]}
                retryLabel={copy.errorRetry}
                title={copy.errorTitle}
            >
                <VatReportPageContent page={page} />
            </ErrorBoundary>
        </div>
    );
};

type VatReportPageContentProps = {
    page: ReturnType<typeof useVatReportPage>;
};

const VatReportPageContent: React.FC<VatReportPageContentProps> = ({
    page,
}) => {
    if (page.error) {
        throw page.error;
    }

    // The View keeps its labels and CTAs while loading and skeletons only the
    // values and rows — the repo's loading-state standard.
    return (
        <VatReportPageView
            isLoading={page.isLoading || page.summary === null}
            generatedText={page.generatedText}
            inputDrillTargets={page.inputDrillTargets}
            inputRows={page.inputRows}
            onDismissWarnings={page.onDismissWarnings}
            onSettled={page.onSettled}
            onViewLedger={page.onViewLedger}
            outputDrillTargets={page.outputDrillTargets}
            outputRows={page.outputRows}
            filingDueDate={page.filingDueDate}
            filingFrom={page.filingFrom}
            filingMismatch={page.filingMismatch}
            filingTo={page.filingTo}
            onShowFilingPeriod={page.onShowFilingPeriod}
            reportedFrom={page.reportedFrom}
            reportedTo={page.reportedTo}
            summary={page.summary}
            warningNotices={page.warningNotices}
        />
    );
};
